import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, 
  Filter,
  Bird,
  PawPrint,
  Turtle,
  Building2,
  Activity,
  Archive,
  RefreshCw,
  ExternalLink,
  Download,
  Trash2
} from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

const ANIMAL_SEARCH_PAGE_SIZE = 50;

export default function AnimalSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(() => {
    const parsed = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  });
  const [bases, setBases] = useState([]);
  const [downloadingAnimalId, setDownloadingAnimalId] = useState(null);
  const canPurgeAnimals = hasPermission(user, 'animals.delete');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [groupFilter, setGroupFilter] = useState(searchParams.get('group') || 'all');
  const [baseFilter, setBaseFilter] = useState(searchParams.get('base') || 'all');

  useEffect(() => {
    fetchBases();
    fetchAnimals();
  }, []);

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (groupFilter !== 'all') params.set('group', groupFilter);
    if (baseFilter !== 'all') params.set('base', baseFilter);
    if (searchTerm.trim()) params.set('search', searchTerm.trim());
    if (currentPage > 1) params.set('page', String(currentPage));
    setSearchParams(params, { replace: true });
  }, [statusFilter, groupFilter, baseFilter, searchTerm, currentPage, setSearchParams]);

  useEffect(() => {
    if (loading) return;
    const scrollKey = `animalSearchScroll:${window.location.search}`;
    const savedScroll = sessionStorage.getItem(scrollKey);
    if (!savedScroll) return;
    window.setTimeout(() => {
      window.scrollTo({ top: Number(savedScroll) || 0, behavior: 'auto' });
      sessionStorage.removeItem(scrollKey);
    }, 0);
  }, [loading]);

  const fetchBases = async () => {
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      console.error('Error fetching bases:', error);
    }
  };

  const fetchAnimals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admissions/summary`);
      setAnimals(response.data);
    } catch (error) {
      toast.error('Erro ao carregar animais');
    } finally {
      setLoading(false);
    }
  };

  const handlePurgeAllAnimals = async () => {
    const confirmation = window.prompt('Digite LIMPAR ANIMAIS para excluir todos os animais e registros vinculados do sistema.');
    if (confirmation !== 'LIMPAR ANIMAIS') {
      toast.error('Confirmação inválida. Nenhum dado foi excluído.');
      return;
    }
    try {
      await axios.post(`${API}/animals/purge-all`, { confirm_text: confirmation });
      toast.success('Todos os animais e registros vinculados foram excluídos.');
      fetchAnimals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao limpar cadastros de animais');
    }
  };

  // Define active vs inactive status
  const ACTIVE_STATUSES = ['Internado', 'Em tratamento', 'Em observação', 'Quarentena'];
  const INACTIVE_STATUSES = ['released', 'death', 'transfer_external', 'escape', 'finalizado', 'Liberado', 'Óbito'];

  const isActive = (animal) => {
    const status = animal.status || '';
    const hasOutcome = animal.outcome && animal.outcome.type;
    return !hasOutcome && ACTIVE_STATUSES.some(s => status.toLowerCase().includes(s.toLowerCase()));
  };

  const filteredAnimals = animals.filter(animal => {
    // Text search
    const matchesSearch = 
      animal.ibama_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      animal.common_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      animal.species?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    let matchesStatus = true;
    if (statusFilter === 'active') {
      matchesStatus = isActive(animal);
    } else if (statusFilter === 'inactive') {
      matchesStatus = !isActive(animal);
    }
    
    // Group filter
    const matchesGroup = groupFilter === 'all' || animal.animal_group === groupFilter;
    
    // Base filter
    const matchesBase = baseFilter === 'all' || animal.base_id === baseFilter;
    
    return matchesSearch && matchesStatus && matchesGroup && matchesBase;
  });

  const totalPages = Math.max(1, Math.ceil(filteredAnimals.length / ANIMAL_SEARCH_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedAnimals = filteredAnimals.slice(
    (safeCurrentPage - 1) * ANIMAL_SEARCH_PAGE_SIZE,
    safeCurrentPage * ANIMAL_SEARCH_PAGE_SIZE
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const updateSearchTerm = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const updateStatusFilter = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const updateGroupFilter = (value) => {
    setGroupFilter(value);
    setCurrentPage(1);
  };

  const updateBaseFilter = (value) => {
    setBaseFilter(value);
    setCurrentPage(1);
  };

  const buildAnimalRecordPath = (animalId) => {
    const returnTo = `/animals${window.location.search || ''}`;
    return `/animals/${animalId}/record?return_to=${encodeURIComponent(returnTo)}`;
  };

  const openAnimalRecord = (animalId) => {
    sessionStorage.setItem(`animalSearchScroll:${window.location.search}`, String(window.scrollY || 0));
    navigate(buildAnimalRecordPath(animalId));
  };

  // Count stats
  const activeCount = animals.filter(isActive).length;
  const inactiveCount = animals.length - activeCount;
  const groupCounts = {
    AVE: animals.filter(a => a.animal_group === 'AVE').length,
    MAM: animals.filter(a => a.animal_group === 'MAM').length,
    REPT: animals.filter(a => a.animal_group === 'REPT').length
  };

  const getGroupBadgeColor = (group) => {
    switch (group) {
      case 'AVE': return 'bg-sky-500 text-white';
      case 'MAM': return 'bg-amber-500 text-white';
      case 'REPT': return 'bg-green-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusBadge = (animal) => {
    // Check animal status first - finalized outcomes take priority
    const status = animal.status?.toLowerCase();
    if (status === 'soltura' || status === 'solto') {
      return <Badge className="bg-emerald-100 text-emerald-700">Solto</Badge>;
    }
    if (status === 'óbito' || status === 'obito') {
      return <Badge className="bg-red-100 text-red-700">Óbito</Badge>;
    }
    if (status === 'eutanásia' || status === 'eutanasia') {
      return <Badge className="bg-red-100 text-red-700">Eutanásia</Badge>;
    }
    if (status === 'transferência' || status === 'transferencia') {
      return <Badge className="bg-purple-100 text-purple-700">Transferido</Badge>;
    }
    if (status === 'alta clínica' || status === 'alta_clinica') {
      return <Badge className="bg-green-100 text-green-700">Alta Clínica</Badge>;
    }
    if (status === 'permanência' || status === 'permanencia') {
      return <Badge className="bg-indigo-100 text-indigo-700">Permanência</Badge>;
    }
    
    // Legacy outcome type check
    if (animal.outcome?.type) {
      switch (animal.outcome.type) {
        case 'release': return <Badge className="bg-emerald-100 text-emerald-700">Solto</Badge>;
        case 'death': return <Badge className="bg-red-100 text-red-700">Óbito</Badge>;
        case 'transfer_external': return <Badge className="bg-purple-100 text-purple-700">Transferido</Badge>;
        case 'escape': return <Badge className="bg-orange-100 text-orange-700">Fuga</Badge>;
        default: return <Badge className="bg-gray-100 text-gray-700">{animal.outcome.type}</Badge>;
      }
    }
    
    if (animal.ready_for_release) {
      return <Badge className="bg-teal-100 text-teal-700">Pronto p/ Soltura</Badge>;
    }
    
    return <Badge className="bg-blue-100 text-blue-700">{animal.status || 'Em tratamento'}</Badge>;
  };

  const getGroupIcon = (group) => {
    switch (group) {
      case 'AVE': return <Bird className="w-4 h-4" />;
      case 'MAM': return <PawPrint className="w-4 h-4" />;
      case 'REPT': return <Turtle className="w-4 h-4" />;
      default: return null;
    }
  };

  const handleDownloadDetailedReport = async (animal) => {
    setDownloadingAnimalId(animal.id);
    try {
      const response = await axios.get(`${API}/animals/${animal.id}/detailed-report/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_individual_${animal.ibama_id || animal.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório individual baixado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao baixar relatório');
    } finally {
      setDownloadingAnimalId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <header className="bg-white shadow-sm border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="hidden md:flex items-center justify-between gap-6">
            <div className="flex items-center space-x-3 min-w-0">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="text-lg md:text-xl font-bold text-emerald-800 truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Busca de Animais
                </h1>
                <p className="text-xs text-emerald-500 truncate">Acompanhamento Veterinário</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canPurgeAnimals && (
                <Button
                  variant="outline"
                  onClick={handlePurgeAllAnimals}
                  className="border-red-300 text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Limpar Animais
                </Button>
              )}
              <Button
                onClick={fetchAnimals}
                variant="outline"
                size="sm"
                disabled={loading}
                className="border-emerald-600 text-emerald-600"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-emerald-600 text-emerald-600"
              >
                Voltar
              </Button>
              <Button variant="ghost" onClick={logout} className="text-red-600">
                Sair
              </Button>
            </div>
          </div>

          <div className="md:hidden space-y-4">
            <div className="flex items-start space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-11 w-11 object-contain"
                />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="text-lg font-bold text-emerald-800 break-words" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Busca de Animais
                </h1>
                <p className="text-xs text-emerald-500 break-words">Acompanhamento Veterinário</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {canPurgeAnimals && (
                <Button
                  onClick={handlePurgeAllAnimals}
                  variant="outline"
                  size="sm"
                  className="w-full border-red-300 text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              <Button
                onClick={fetchAnimals}
                variant="outline"
                size="sm"
                disabled={loading}
                className="w-full border-emerald-600 text-emerald-600"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full border-emerald-600 text-emerald-600"
              >
                Voltar
              </Button>
              <Button variant="ghost" onClick={logout} className="w-full text-red-600">
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Status Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card 
            className={`cursor-pointer transition-all ${statusFilter === 'all' ? 'ring-2 ring-emerald-500' : 'hover:shadow-md'}`}
            onClick={() => updateStatusFilter('all')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">{animals.length}</p>
              <p className="text-xs text-gray-500">Total</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${statusFilter === 'active' ? 'ring-2 ring-emerald-500' : 'hover:shadow-md'}`}
            onClick={() => updateStatusFilter('active')}
          >
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 text-emerald-600 mb-1">
                <Activity className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
              <p className="text-xs text-gray-500">Ativos</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${statusFilter === 'inactive' ? 'ring-2 ring-emerald-500' : 'hover:shadow-md'}`}
            onClick={() => updateStatusFilter('inactive')}
          >
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 text-gray-500 mb-1">
                <Archive className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold text-gray-500">{inactiveCount}</p>
              <p className="text-xs text-gray-500">Finalizados</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${groupFilter === 'AVE' ? 'ring-2 ring-sky-500' : 'hover:shadow-md'}`}
            onClick={() => updateGroupFilter(groupFilter === 'AVE' ? 'all' : 'AVE')}
          >
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 text-sky-500 mb-1">
                <Bird className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold text-sky-600">{groupCounts.AVE}</p>
              <p className="text-xs text-gray-500">Aves</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${groupFilter === 'MAM' ? 'ring-2 ring-amber-500' : 'hover:shadow-md'}`}
            onClick={() => updateGroupFilter(groupFilter === 'MAM' ? 'all' : 'MAM')}
          >
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 text-amber-500 mb-1">
                <PawPrint className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold text-amber-600">{groupCounts.MAM}</p>
              <p className="text-xs text-gray-500">Mamíferos</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-emerald-100">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Label className="flex items-center gap-2 mb-2">
                  <Search className="w-4 h-4" /> Buscar
                </Label>
                <Input
                  placeholder="ID IBAMA, nome ou espécie..."
                  value={searchTerm}
                  onChange={(e) => updateSearchTerm(e.target.value)}
                  className="min-h-[44px]"
                  data-testid="search-input"
                />
              </div>
              <div className="w-full md:w-48">
                <Label className="flex items-center gap-2 mb-2">
                  <Filter className="w-4 h-4" /> Status
                </Label>
                <Select value={statusFilter} onValueChange={updateStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">Ativos</SelectItem>
                    <SelectItem value="inactive">Finalizados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full md:w-48">
                <Label className="flex items-center gap-2 mb-2">
                  <PawPrint className="w-4 h-4" /> Grupo
                </Label>
                <Select value={groupFilter} onValueChange={updateGroupFilter}>
                  <SelectTrigger data-testid="group-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="AVE">Aves</SelectItem>
                    <SelectItem value="MAM">Mamíferos</SelectItem>
                    <SelectItem value="REPT">Répteis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full md:w-48">
                <Label className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4" /> Base
                </Label>
                <Select value={baseFilter} onValueChange={updateBaseFilter}>
                  <SelectTrigger data-testid="base-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {bases.map(base => (
                      <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-emerald-100">
          <CardHeader>
            <CardTitle className="text-emerald-800 flex items-center justify-between">
              <span>Resultados ({filteredAnimals.length})</span>
              {(statusFilter !== 'all' || groupFilter !== 'all' || baseFilter !== 'all' || searchTerm.trim()) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setGroupFilter('all');
                    setBaseFilter('all');
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  className="text-gray-500"
                >
                  Limpar filtros
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                <p className="mt-4 text-gray-500">Carregando...</p>
              </div>
            ) : filteredAnimals.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nenhum animal encontrado</p>
                <p className="text-sm text-gray-400 mt-2">Tente ajustar os filtros</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID IBAMA</TableHead>
                      <TableHead className="hidden md:table-cell">PMP</TableHead>
                      <TableHead>Nome Popular</TableHead>
                      <TableHead>Espécie</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Base</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAnimals.map((animal) => (
                      <TableRow 
                        key={animal.id} 
                        className="cursor-pointer hover:bg-emerald-50"
                        onClick={() => openAnimalRecord(animal.id)}
                        data-testid={`animal-row-${animal.id}`}
                      >
                        <TableCell className="font-medium text-emerald-700">
                          {animal.ibama_id}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {animal.pmp_id || '-'}
                        </TableCell>
                        <TableCell>{animal.common_name}</TableCell>
                        <TableCell className="text-gray-600 italic">{animal.species}</TableCell>
                        <TableCell>
                          <Badge className={getGroupBadgeColor(animal.animal_group)}>
                            {getGroupIcon(animal.animal_group)}
                            <span className="ml-1">{animal.animal_group}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(animal)}</TableCell>
                        <TableCell className="text-sm text-gray-500">{animal.base_name}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadDetailedReport(animal);
                              }}
                              className="text-sky-600"
                              disabled={downloadingAnimalId === animal.id}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAnimalRecord(animal.id);
                              }}
                              className="text-emerald-600"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {!loading && filteredAnimals.length > ANIMAL_SEARCH_PAGE_SIZE && (
              <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-500">
                  Página {safeCurrentPage} de {totalPages} • {filteredAnimals.length} animais
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={safeCurrentPage <= 1}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  >
                    Anterior
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={safeCurrentPage >= totalPages}
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
